import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Label, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, SquareDashedIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitScanInput } from '../components/HandlingUnitScanInput';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { StockItemScanInput } from '../components/StockItemScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';
import { useTerminalState } from './useTerminalState.hook';

const formSchema = z.object({
	stockItem: z
		.object({
			id: z.number()
		})
		.nullable(),
	location: z
		.object({
			id: z.number()
		})
		.nullable(),
	outboundHandlingUnit: z
		.object({
			id: z.number()
		})
		.nullable(),
	sourceHandlingUnit: z
		.object({
			id: z.number()
		})
		.nullable(),
	quantity: z.number().nullable()
});

type FormValues = z.infer<typeof formSchema>;

const getColorState = (inputValue: { id: number } | null, suggestedValue: { id: number } | null) => {
	if (!inputValue) return 'bg-transparent';
	if (inputValue.id === suggestedValue?.id) return 'bg-green-200';
	return 'bg-red-200';
};

export default function OutboundWavelessProcessPage() {
	const documentTypeId = useNumberParam('documentTypeId');
	const queueId = useNumberParam('queueId');
	const client = useTerminalClient();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<'sourceHandlingUnit' | 'location' | 'stockItem' | 'quantity'>('sourceHandlingUnit');

	const form = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			stockItem: null,
			location: null,
			outboundHandlingUnit: null,
			sourceHandlingUnit: null,
			quantity: null
		}
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { errors }
	} = form;

	const watchedValues = watch();
	const { data: terminalState } = useTerminalState();
	const activeLocation = terminalState?.activeWavelessPickingStockItemLocation;
	const activeQuantity = terminalState?.activeWavelessPickingStockItemQuantity;

	// Color states for validation
	const handlingUnitColorState = getColorState(watchedValues.sourceHandlingUnit, activeLocation?.handlingUnit);
	const sourceLocationColorState = getColorState(watchedValues.location, activeLocation?.storageLocation);
	const stockItemColorState = getColorState(watchedValues.stockItem, activeLocation?.stockItem);

	// Auto-tab progression logic
	useEffect(() => {
		if (watchedValues.sourceHandlingUnit) {
			if (watchedValues.sourceHandlingUnit?.storageLocation) {
				setActiveTab('stockItem');
			} else {
				setActiveTab('location');
			}
		}
	}, [watchedValues.sourceHandlingUnit]);

	useEffect(() => {
		if (watchedValues.location) {
			setActiveTab('stockItem');
		}
	}, [watchedValues.location]);

	useEffect(() => {
		if (watchedValues.stockItem) {
			setActiveTab('quantity');
		}
	}, [watchedValues.stockItem]);

	// Initialize outbound handling unit from terminal state
	useEffect(() => {
		if (terminalState?.pickingHandlingUnit && !watchedValues.outboundHandlingUnit) {
			setValue('outboundHandlingUnit', terminalState.pickingHandlingUnit);
		}
	}, [terminalState?.pickingHandlingUnit, watchedValues.outboundHandlingUnit, setValue]);

	// Auto-fill location when source handling unit has storage location
	useEffect(() => {
		if (watchedValues.sourceHandlingUnit?.storageLocation) {
			setValue('location', watchedValues.sourceHandlingUnit.storageLocation);
		}
	}, [watchedValues.sourceHandlingUnit, setValue]);

	// Mutations
	const loadMutation = useMutation({
		mutationFn: async (data: FormValues) => {
			await client.outboundDeliveryOrders.outboundWavelessLoadPickMutation.execute({
				documentTypeId: documentTypeId!,
				stockItemId: data.stockItem?.id,
				storeLocationId: data.location?.id,
				sourceHandlingUnitId: data.sourceHandlingUnit?.id,
				pickingHandlingUnitId: data.outboundHandlingUnit?.id,
				quantity: data.quantity ?? 0
			});
		},
		onSuccess: () => {
			navigate(`/outbound/${documentTypeId}/waveless/${queueId}`);
		}
	});

	const leavePickMutation = useMutation({
		mutationFn: async () => {
			await client.outboundDeliveryOrders.outboundWavelessLeavePickMutation.execute({});
		},
		onSuccess: () => {
			navigate(-1);
		}
	});

	useValidationResultDialog([loadMutation.error, leavePickMutation.error]);

	// Form handlers
	const handleOutboundHandlingUnitChange = (handlingUnit: any) => {
		setValue('outboundHandlingUnit', handlingUnit);
		if (handlingUnit) {
			setActiveTab('sourceHandlingUnit');
		}
	};

	// Custom back button with leave pick functionality
	const customBackButton = useMemo(
		() => (
			<Button variant="ghost" size="icon" onClick={() => leavePickMutation.mutate()} disabled={leavePickMutation.isPending} loading={leavePickMutation.isPending}>
				<ArrowLeft className="h-4 w-4" />
			</Button>
		),
		[leavePickMutation]
	);

	const confirmLoad = useCallback(() => handleSubmit(data => loadMutation.mutate(data))(), [handleSubmit, loadMutation]);

	const disabledLoad = useMemo(() => {
		return loadMutation.isPending || !documentTypeId || !watchedValues.quantity;
	}, [loadMutation, documentTypeId, watchedValues.quantity]);

	const bottomContent = useMemo(() => {
		return (
			<div className="flex flex-col gap-2 p-2">
				<Button className="w-full" onClick={confirmLoad} disabled={disabledLoad}>
					Potvrdit položku
				</Button>
			</div>
		);
	}, [confirmLoad, disabledLoad]);

	return (
		<TerminalScreen
			showBackButton={false}
			hideBackButton={true}
			customBackButton={customBackButton}
			topBar={<StockItemLinkDetail stockItemId={activeLocation?.stockItem?.id} />}
			bottomContent={bottomContent}
		>
			<div className="flex flex-col gap-2 h-full overflow-y-auto">
				{/* Active picking information */}
				{activeLocation && (
					<div className="flex flex-col gap-2 p-2">
						<Label>Vychystávací paleta</Label>
						<HandlingUnitScanInput
							placeholder="Vychystávací paleta..."
							value={watchedValues.outboundHandlingUnit}
							onChange={handleOutboundHandlingUnitChange}
							autoFocus
						/>
						<div className="flex flex-col bg-white p-2 shadow-md gap-2">
							<div className="space-y-2">
								<div className="flex flex-row">
									<div className="text-sm text-muted-foreground flex-1">Lokace</div>
									<div className="font-semibold flex-1">{activeLocation.storageLocation.name}</div>
								</div>
								<div className="flex flex-row">
									<div className="text-sm text-muted-foreground flex-1">Paleta</div>
									<div className="font-semibold flex-1">{activeLocation.handlingUnit?.code ?? '-'}</div>
								</div>
								<div className="flex flex-row">
									<div className="text-sm text-muted-foreground flex-1">Šarže</div>
									<div className="font-semibold flex-1">{activeLocation.batch ?? '-'}</div>
								</div>
								<div className="flex flex-row">
									<div className="text-sm text-muted-foreground flex-1">Zbývá</div>
									<div className="font-bold flex-1">{activeQuantity ?? 0} ks</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Input tabs */}
				<div className="flex-1 flex flex-col bg-white p-2 shadow-md gap-2">
					{watchedValues.outboundHandlingUnit ? (
						<Tabs value={activeTab} onValueChange={v => setActiveTab(v)} className="w-full">
							<TabsList className="grid grid-cols-4 w-full">
								<TabsTrigger value="sourceHandlingUnit" className={handlingUnitColorState}>
									Paleta
								</TabsTrigger>
								<TabsTrigger value="location" className={sourceLocationColorState}>
									Lokace
								</TabsTrigger>
								<TabsTrigger value="stockItem" className={stockItemColorState}>
									SKU
								</TabsTrigger>
								<TabsTrigger value="quantity">Množství</TabsTrigger>
							</TabsList>

							<TabsContent value="sourceHandlingUnit" className="space-y-4 p-2">
								<HandlingUnitScanInput
									placeholder="Naskenujte paletu položky..."
									value={watchedValues.sourceHandlingUnit}
									onChange={value => setValue('sourceHandlingUnit', value)}
									autoFocus
								/>
							</TabsContent>

							<TabsContent value="location" className="space-y-4 p-2">
								<StorageLocationScanInput
									placeholder="Naskenujte lokaci..."
									value={watchedValues.location}
									onChange={value => setValue('location', value)}
									autoFocus
								/>
							</TabsContent>

							<TabsContent value="stockItem" className="space-y-4 p-2">
								<StockItemScanInput value={watchedValues.stockItem} onChange={value => setValue('stockItem', value)} autoFocus />
							</TabsContent>

							<TabsContent value="quantity" className="space-y-4 p-2">
								<Input
									type="number"
									value={watchedValues.quantity ?? ''}
									onChange={e => setValue('quantity', Number(e.target.value))}
									placeholder="Zadejte množství"
									autoFocus
								/>
							</TabsContent>
						</Tabs>
					) : (
						<div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
							<SquareDashedIcon className="w-10 h-10" />
							<div className="text-muted-foreground">Zadejte vychystávací paletu</div>
						</div>
					)}
				</div>
			</div>
		</TerminalScreen>
	);
}
